/**
 * Edit workflow — /settings/custom-modules/$key/workflows/$workflowKey
 *
 * Loads the workflow, prefills the editor, saves via update mutation,
 * and exposes a Run history tab showing recent runs from
 * modules.custom.workflows.listRuns.
 */

import type { FieldDefinition } from '@cloud/custom-modules'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import {
  AlertTriangle,
  ArrowLeft,
  Check,
  ChevronDown,
  ChevronRight,
  Clock,
  OctagonX,
  Pause,
  Trash2,
} from 'lucide-react'
import { useEffect, useState } from 'react'
import { toast } from 'sonner'
import {
  type EntitySummary,
  WorkflowEditor,
  type WorkflowFormValue,
  type WorkflowStepSpec,
  type WorkflowTriggerSpec,
} from '@/components/custom/workflow-editor'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Skeleton } from '@/components/ui/skeleton'
import { orpc } from '@/lib/orpcClient'

export const Route = createFileRoute('/settings/custom-modules/$key/workflows/$workflowKey')({
  component: EditWorkflowPage,
})

function EditWorkflowPage() {
  const { key: moduleKey, workflowKey } = Route.useParams()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [deleteOpen, setDeleteOpen] = useState(false)

  const { data: workflow, isLoading } = useQuery(
    orpc.modules.custom.workflows.get.queryOptions({
      input: { moduleKey, key: workflowKey },
    }),
  )

  // Entities MUST be loaded before we render the editor — Radix Select
  // resolves its displayed label from the SelectItem children available
  // at the time the value changes, and does NOT re-resolve when items
  // appear asynchronously. If we let the editor mount with entities=[],
  // the "On entity" dropdown renders with value='audits' but no matching
  // item, shows empty, and stays that way even after entities load. So
  // we gate the whole editor on both queries being ready.
  const { data: entitiesRaw, isLoading: entitiesLoading } = useQuery(
    orpc.modules.custom.entities.list.queryOptions({ input: { moduleKey } }),
  )

  // P2.2: registered action manifests, used to populate the step-type
  // dropdown. Same Radix constraint applies — gate the editor on this
  // being loaded so the Select mounts with the full item list.
  const { data: availableActions, isLoading: actionsLoading } = useQuery(
    orpc.modules.custom.workflows.listActions.queryOptions(),
  )
  const entities: EntitySummary[] = (entitiesRaw ?? []).map((e) => ({
    key: e.key,
    name: e.name,
    namePlural: e.namePlural,
    fields: e.fields as FieldDefinition[],
  }))

  const [form, setForm] = useState<WorkflowFormValue>({
    name: '',
    description: '',
    enabled: true,
    trigger: { type: 'record.created' },
    steps: [],
  })

  // Hydrate the form from the server workflow ONCE per identity.
  //
  // Depending on `workflow` directly would reset local edits on every
  // refetch (window focus, invalidation, stale-time), silently nuking
  // unsaved changes like a freshly picked entity or condition. Keying
  // off `workflow?.id` ensures we only hydrate on the initial load or
  // when navigating to a different workflow.
  useEffect(() => {
    if (workflow) {
      setForm({
        name: workflow.name,
        description: workflow.description ?? '',
        enabled: workflow.enabled,
        trigger: workflow.trigger as WorkflowTriggerSpec,
        steps: workflow.steps as WorkflowStepSpec[],
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workflow?.id])

  const updateMutation = useMutation(
    orpc.modules.custom.workflows.update.mutationOptions({
      onSuccess: () => {
        toast.success('Workflow saved')
        void queryClient.invalidateQueries({
          queryKey: orpc.modules.custom.workflows.get.queryKey({
            input: { moduleKey, key: workflowKey },
          }),
        })
        void queryClient.invalidateQueries({
          queryKey: orpc.modules.custom.workflows.list.queryKey({
            input: { moduleKey },
          }),
        })
      },
      onError: (err) => toast.error(err.message),
    }),
  )

  const deleteMutation = useMutation(
    orpc.modules.custom.workflows.delete.mutationOptions({
      onSuccess: () => {
        toast.success('Workflow deleted')
        void queryClient.invalidateQueries({
          queryKey: orpc.modules.custom.workflows.list.queryKey({
            input: { moduleKey },
          }),
        })
        void navigate({
          to: '/settings/custom-modules/$key/workflows',
          params: { key: moduleKey },
        })
      },
      onError: (err) => toast.error(err.message),
    }),
  )

  if (isLoading || entitiesLoading || actionsLoading) {
    return <Skeleton className="h-96 w-full" />
  }

  if (!workflow) {
    return <p className="text-muted-foreground">Workflow not found.</p>
  }

  // NOTE: this page deliberately does NOT wrap the editor in a <form>.
  // Radix Select's "form control" branch (isFormControl = trigger is
  // inside a form) renders a hidden native <select> to bubble the value
  // into the form. Its options come from an asynchronously populated
  // collection, so on first hydration the options are empty, the browser
  // snaps the native select's value to '', and Radix fires onValueChange
  // with '' — wiping our controlled state. Using a div + button onClick
  // avoids the form-control branch entirely.
  const handleSave = () => {
    updateMutation.mutate({
      moduleKey,
      key: workflowKey,
      name: form.name,
      description: form.description || null,
      enabled: form.enabled,
      trigger: form.trigger,
      steps: form.steps,
    })
  }

  return (
    <div className="space-y-4">
      <Link
        to="/settings/custom-modules/$key/workflows"
        params={{ key: moduleKey }}
        className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft className="h-4 w-4 mr-1" />
        Back to workflows
      </Link>

      <div className="space-y-4">
        {workflow.disabledReason && (
          <Card className="border-destructive/50 bg-destructive/5">
            <CardContent className="flex items-start gap-3 py-4">
              <AlertTriangle className="h-5 w-5 text-destructive flex-shrink-0 mt-0.5" />
              <div className="space-y-1">
                <p className="text-sm font-medium text-destructive">Auto-paused by the system</p>
                <p className="text-sm text-muted-foreground">
                  {workflow.disabledReason}. Edit the steps below to remove the broken reference,
                  or ask an admin to reinstall the module. Saving any change to the steps clears
                  this state.
                </p>
              </div>
            </CardContent>
          </Card>
        )}
        <WorkflowEditor
          value={form}
          onChange={setForm}
          entities={entities}
          availableActions={availableActions ?? []}
        />

        <div className="flex justify-end gap-2">
          <Button
            type="button"
            variant="destructive"
            onClick={() => setDeleteOpen(true)}
            className="mr-auto"
          >
            <Trash2 className="h-4 w-4 mr-2" />
            Delete
          </Button>
          <Link
            to="/settings/custom-modules/$key/workflows"
            params={{ key: moduleKey }}
          >
            <Button type="button" variant="outline">
              Cancel
            </Button>
          </Link>
          <Button type="button" onClick={handleSave} disabled={updateMutation.isPending}>
            {updateMutation.isPending ? 'Saving…' : 'Save changes'}
          </Button>
        </div>
      </div>

      <RunHistoryCard moduleKey={moduleKey} workflowKey={workflowKey} />

      <Dialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete "{workflow.name}"?</DialogTitle>
            <DialogDescription>
              This permanently deletes the workflow and all its run history. This cannot be
              undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              disabled={deleteMutation.isPending}
              onClick={() =>
                deleteMutation.mutate({ moduleKey, key: workflowKey })
              }
            >
              {deleteMutation.isPending ? 'Deleting…' : 'Delete permanently'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}

// ---------- Run history card ----------

function RunHistoryCard({
  moduleKey,
  workflowKey,
}: {
  moduleKey: string
  workflowKey: string
}) {
  const { data, isLoading } = useQuery(
    orpc.modules.custom.workflows.listRuns.queryOptions({
      input: { moduleKey, workflowKey, page: 1, pageSize: 20 },
    }),
  )

  const [expandedRunId, setExpandedRunId] = useState<string | null>(null)

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Recent runs</CardTitle>
        <p className="text-sm text-muted-foreground">
          Last 20 runs of this workflow, newest first.
        </p>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <Skeleton className="h-32 w-full" />
        ) : !data || data.runs.length === 0 ? (
          <p className="text-sm text-muted-foreground text-center py-4">
            No runs yet. Trigger a record event that matches this workflow to see it fire.
          </p>
        ) : (
          <ul className="divide-y">
            {data.runs.map((run) => {
              const isExpanded = expandedRunId === run.id
              const steps = (run.stepsExecuted ?? []) as StepLogEntry[]
              const duration =
                run.finishedAt && run.startedAt
                  ? Math.round(
                      (new Date(run.finishedAt).getTime() -
                        new Date(run.startedAt).getTime()),
                    )
                  : null
              return (
                <li key={run.id} className="py-2">
                  <button
                    type="button"
                    onClick={() => setExpandedRunId(isExpanded ? null : run.id)}
                    className="w-full text-left flex items-center justify-between gap-3"
                  >
                    <div className="flex items-center gap-3 text-sm">
                      <StatusBadge status={run.status} />
                      <span className="text-muted-foreground text-xs">
                        {new Date(run.startedAt).toLocaleString()}
                      </span>
                      {duration !== null && (
                        <span className="text-muted-foreground text-xs flex items-center gap-1">
                          <Clock className="h-3 w-3" />
                          {duration < 1000
                            ? `${duration}ms`
                            : `${(duration / 1000).toFixed(1)}s`}
                        </span>
                      )}
                      {run.error && (
                        <span className="text-destructive text-xs truncate max-w-md">
                          {run.error}
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-2 text-xs text-muted-foreground">
                      <span>{steps.length} steps</span>
                      {isExpanded ? (
                        <ChevronDown className="h-3 w-3" />
                      ) : (
                        <ChevronRight className="h-3 w-3" />
                      )}
                    </div>
                  </button>
                  {isExpanded && steps.length > 0 && (
                    <div className="mt-2 space-y-1 pl-2 border-l-2 border-border ml-3">
                      {steps.map((step, i) => (
                        <StepLogRow key={`${run.id}-${i}`} step={step} />
                      ))}
                    </div>
                  )}
                  {isExpanded && steps.length === 0 && (
                    <p className="mt-2 text-xs text-muted-foreground italic pl-6">
                      No step data recorded for this run.
                    </p>
                  )}
                </li>
              )
            })}
          </ul>
        )}
      </CardContent>
    </Card>
  )
}

// ---------- Step log types + renderer ----------

interface StepLogEntry {
  stepId: string
  stepType: string
  status: 'success' | 'failure' | 'halt'
  startedAt: string
  finishedAt: string
  output?: unknown
  error?: string
}

function StepLogRow({ step }: { step: StepLogEntry }) {
  const [expanded, setExpanded] = useState(false)
  const duration =
    step.startedAt && step.finishedAt
      ? Math.round(
          new Date(step.finishedAt).getTime() - new Date(step.startedAt).getTime(),
        )
      : null

  const StatusIcon =
    step.status === 'success'
      ? Check
      : step.status === 'halt'
        ? Pause
        : OctagonX
  const iconColor =
    step.status === 'success'
      ? 'text-emerald-600'
      : step.status === 'halt'
        ? 'text-amber-500'
        : 'text-destructive'

  return (
    <div className="py-1.5 pl-3">
      <button
        type="button"
        onClick={() => setExpanded(!expanded)}
        className="w-full text-left flex items-center gap-2 text-xs group"
      >
        <StatusIcon className={`h-3.5 w-3.5 flex-shrink-0 ${iconColor}`} />
        <span className="font-mono text-muted-foreground">{step.stepId}</span>
        <Badge variant="outline" className="text-[10px] font-normal px-1.5 py-0">
          {step.stepType}
        </Badge>
        {duration !== null && (
          <span className="text-muted-foreground/60 ml-auto">
            {duration < 1000 ? `${duration}ms` : `${(duration / 1000).toFixed(1)}s`}
          </span>
        )}
      </button>
      {step.error && (
        <p className="mt-0.5 pl-5 text-xs text-destructive">{step.error}</p>
      )}
      {expanded && step.output !== undefined && step.output !== null && (
        <pre className="mt-1 pl-5 rounded bg-muted p-2 text-[11px] overflow-x-auto whitespace-pre-wrap max-h-40">
          {typeof step.output === 'string'
            ? step.output
            : JSON.stringify(step.output, null, 2)}
        </pre>
      )}
    </div>
  )
}

function StatusBadge({
  status,
}: {
  status: 'pending' | 'running' | 'success' | 'failed' | 'timeout'
}) {
  const variant: Parameters<typeof Badge>[0]['variant'] =
    status === 'success'
      ? 'default'
      : status === 'failed' || status === 'timeout'
        ? 'destructive'
        : 'secondary'
  return (
    <Badge variant={variant} className="text-xs">
      {status}
    </Badge>
  )
}
